import * as SecureStore from "expo-secure-store";
import { ClerkProvider, SignedIn, SignedOut, useAuth, useUser, useClerk, ClerkLoaded } from "@clerk/clerk-expo";
import React, { useEffect } from "react";
import { Text } from "react-native";

const publishableKey = process.env.EXPO_PUBLIC_CLERK_PUBLISHABLE_KEY!;

if (!publishableKey) {
	throw new Error("Missing Publishable Key. Please set EXPO_PUBLIC_CLERK_PUBLISHABLE_KEY in your .env");
}

const tokenCache = {
	async getToken(key: string) {
		try {
			const item = await SecureStore.getItemAsync(key);
			if (item) {
				console.log(`${key} was used 🔐 \n`);
			} else {
				console.log("No values stored under key: " + key);
			}
			return item;
		} catch (error) {
			console.error("SecureStore get item error: ", error);
			await SecureStore.deleteItemAsync(key);
			return null;
		}
	},
	async saveToken(key: string, value: string) {
		try {
			return SecureStore.setItemAsync(key, value);
		} catch (err) {
			return;
		}
	},
};

const AbcoClerkProvider = ({ children }: AbcoClerkProviderProps) => {
	// If there is no window object, we are likely in a server-side environment (e.g., Next.js SSR), so we return null to avoid errors.
    if (typeof window === "undefined") {
        return null; // Or your loading component
    }

	const ClerkWrapper = ({ children }: { children: React.ReactNode }) => {
		useEffect(() => {
			const removeClerkSynced = () => {
				const url = new URL(window.location.href);
				if (url.searchParams.has("__clerk_synced")) {
					url.searchParams.delete("__clerk_synced");
					// Preserve the full URL including hash fragment
					const newUrl = url.pathname + url.search + url.hash;
					window.history.replaceState({}, document.title, newUrl);
				}
			};

			// Remove immediately
			removeClerkSynced();

			// Also check after a short delay to catch any late additions
			const timeoutId = setTimeout(removeClerkSynced, 100);

			return () => clearTimeout(timeoutId);
		}, []);

		return <>{children}</>;
	};

	const getDomain = () => {
		const hostname = window.location.hostname;

		if (hostname.includes("abcopro.co.nz")) {
			return "abcopro.co.nz";
		} else if (hostname.includes("abcopro.com.au")) {
			return "abcopro.com.au";
		}
		
		return null;
	};

	var domain = getDomain();

	if( domain == null ) {
		return (
			<ClerkProvider 
			tokenCache={tokenCache} 
			publishableKey={publishableKey}>
				<ClerkLoaded>
					<ClerkWrapper>{children}</ClerkWrapper>
				</ClerkLoaded>
			</ClerkProvider>
		);
	}

	return (
		<ClerkProvider 
			tokenCache={tokenCache} 
			publishableKey={publishableKey} 
			isSatellite={true} 
			signInUrl="https://accounts.abcopro.com/sign-in" 
			signUpUrl="https://accounts.abcopro.com/sign-up" 
			routerPush={(url) => console.log("routerPush", url)} 
			routerReplace={(url) => console.log("routerReplace", url)} 
			signInForceRedirectUrl={"/"}
			domain={domain}
		>
			<ClerkLoaded>
				<ClerkWrapper>{children}</ClerkWrapper>
			</ClerkLoaded>
		</ClerkProvider>
	);
};

export { AbcoClerkProvider, SignedIn, SignedOut, useAuth, useUser, useClerk };
